import { Box, Checkbox } from "@mui/material"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import type { ReactElement } from "react"

import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { LongWordWrapper } from "@/components/table/LongWordWrapper"
import type { InstructionProduct, Product } from "@/graphql/codegen/graphql"
import { ComputedProductStatusMap } from "@/screens/Companies/products/utils/productStatusMap"
import TableCellWithId from "@/screens/ManageProducts/components/Instructions/components/tables/TableCellWithId"

export type ProductEntity = InstructionProduct | Product

export const columns: ColumnDef<ProductEntity>[] = [
  {
    accessorKey: "productId",
    id: "productId",
    header: "",
    cell: ({ row }: CellContext<ProductEntity, unknown>): ReactElement => {
      return (
        <Box className='space-x-5'>
          <Checkbox
            id={row.original.productId.toString()}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            disabled={!row.getCanSelect()}
          />
        </Box>
      )
    },
    size: 50,
  },
  {
    header: "Product ID",
    cell: ({ row }: CellContext<ProductEntity, unknown>): ReactElement => (
      <div className='flex items-center gap-2'>
        <Box className='mx-2'>
          <StatusIndicator
            data-testid={`statusIndicator-${row?.original?.computedProductStatus}`}
            status={row?.original?.computedProductStatus as string}
            map={ComputedProductStatusMap}
          />
        </Box>

        <TableCellWithId id={row?.original?.externalSystemId}>{row?.original?.publicId}</TableCellWithId>
      </div>
    ),
    enableSorting: false,
  },
  {
    accessorKey: "companyProductDescription",
    header: "Akrochem Desc.",
    enableSorting: false,
    size: 128,
    cell: ({ row }: CellContext<ProductEntity, unknown>): ReactElement => (
      <LongWordWrapper>{row.original.companyProductDescription}</LongWordWrapper>
    ),
  },
  {
    accessorKey: "vendorDescription",
    header: "Supplier Desc.",
    enableSorting: false,
    size: 128,
    cell: ({ row }: CellContext<ProductEntity, unknown>): ReactElement => (
      <LongWordWrapper>{row.original.vendorDescription}</LongWordWrapper>
    ),
  },
  {
    header: "Supplier Name",
    enableSorting: false,
    cell: ({ row }: CellContext<ProductEntity, unknown>): ReactElement => (
      <div className='flex items-center gap-2'>
        <TableCellWithId id={row.original?.company?.externalSystemId}>
          <LongWordWrapper>{row.original?.company?.name}</LongWordWrapper>
        </TableCellWithId>
      </div>
    ),
  },
  {
    header: "Category",
    enableSorting: false,
    cell: ({ row }: CellContext<ProductEntity, unknown>): ReactElement => (
      <div className='flex items-center gap-2'>
        <TableCellWithId
          id={
            (row.original as InstructionProduct)?.category?.externalSystemId ??
            (row.original as Product)?.classification?.categoryDetail?.externalSystemId
          }
        >
          <LongWordWrapper>
            {(row.original as InstructionProduct)?.category?.name ??
              (row.original as Product)?.classification?.categoryDetail?.name}
          </LongWordWrapper>
        </TableCellWithId>
      </div>
    ),
  },
  {
    header: "Class",
    enableSorting: false,
    cell: ({ row }: CellContext<ProductEntity, unknown>): ReactElement => (
      <div className='flex items-center gap-2'>
        <TableCellWithId id={row.original?.classification?.externalSystemId}>
          <LongWordWrapper>{row.original?.classification?.name}</LongWordWrapper>
        </TableCellWithId>
      </div>
    ),
  },
]
